import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GoogleLogo, AppleLogo, ShieldCheck, Sparkle, Package,
  MapPin, QrCode, Microphone, House, Camera, ArrowRight, DeviceMobile,
} from "@phosphor-icons/react";
import MarketingLayout from "@/components/MarketingLayout";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=app.nestled6035650c&pcampaignid=web_share";

const FEATURES = [
  { icon: Camera, title: "Snap it, name it, done",
    body: "Point your camera at anything worth remembering. Nestled files it away with a photo, a name, and where it lives." },
  { icon: MapPin, title: "Rooms inside rooms",
    body: "Kitchen › Pantry › Top shelf. Nest locations as deep as your home actually goes — every bin, every drawer." },
  { icon: QrCode, title: "Print QR labels",
    body: "Stick one on a bin, scan it later with your phone, see everything inside without opening a thing." },
  { icon: Microphone, title: "Talk, don't type",
    body: "Hold the mic and say what you just tucked away. Voice-to-text turns your words into an item, hands-free." },
  { icon: House, title: "Share with your household",
    body: "Family plan lets everyone in the house add, edit, and find things — one home, everyone on the same page." },
  { icon: ShieldCheck, title: "Your data, your call",
    body: "Delete your account and everything goes with it. Cancel anytime. No hidden strings, no ads." },
];

export default function Download() {
  return (
    <MarketingLayout
      title="Download Nestled on Google Play — the cozy home inventory app"
      description="Nestled is now on Google Play. Track where every little thing lives, right from your Android phone. Free forever for your first 25 items."
    >
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-terracotta-50 blur-3xl opacity-70" aria-hidden />
        <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-sage-400/15 blur-3xl" aria-hidden />

        <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 grid lg:grid-cols-[1.15fr,1fr] gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-sage-600">
              <Sparkle weight="fill" size={14} /> Now on Google Play
            </span>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-stoneink-900">
              Your home, in your pocket.
            </h1>
            <p className="mt-5 text-lg text-stoneink-700 max-w-2xl leading-relaxed">
              Nestled is now live on the Play Store. Snap a photo of anything you tuck away —
              from the winter mittens to the router password sticky — and never lose track again.
              Free forever for your first 25 items.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-google-play-btn"
                className="group h-14 pl-4 pr-6 rounded-2xl bg-stoneink-900 hover:bg-stoneink-900/90 text-white flex items-center gap-3 transition-all active:scale-[0.98] shadow-hover"
              >
                <GoogleLogo weight="fill" size={28} className="text-white" />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-wider opacity-80">Get it on</div>
                  <div className="text-lg font-semibold">Google Play</div>
                </div>
                <ArrowRight weight="bold" size={16} className="ml-1 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
              </a>
              <button
                disabled
                data-testid="download-app-store-btn"
                className="h-14 pl-4 pr-6 rounded-2xl border border-sand-300 bg-white/60 text-stoneink-500 flex items-center gap-3 cursor-not-allowed"
                title="iOS coming soon — join the waitlist"
              >
                <AppleLogo weight="fill" size={28} />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-wider">Coming soon</div>
                  <div className="text-lg font-semibold">App Store</div>
                </div>
              </button>
            </div>

            <p className="mt-4 text-sm text-stoneink-500">
              iPhone user? <Link to="/beta" data-testid="download-ios-waitlist-link" className="text-terracotta-600 hover:text-terracotta-700 font-medium underline-offset-4 hover:underline">Join the iOS waitlist</Link> — we&apos;ll email the moment it&apos;s live.
            </p>
          </motion.div>

          {/* Phone mockup showcase */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="relative mx-auto max-w-sm w-full"
          >
            <div className="relative rounded-[42px] border-[10px] border-stoneink-900 bg-stoneink-900 shadow-hover overflow-hidden aspect-[9/19]">
              <img
                src="https://customer-assets.emergentagent.com/job_house-organizer-2/artifacts/67zzomwo_Screenshot_20260619-111559.png"
                alt="Nestled on Android showing a kitchen inventory"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 rounded-2xl bg-white shadow-hover px-4 py-3 border border-sand-300 hidden sm:flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-sage-500 animate-pulse" />
              <span className="text-xs font-medium text-stoneink-900">Live on Play Store</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features grid */}
      <section className="relative max-w-6xl mx-auto px-6 md:px-10 pb-20">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-terracotta-600">Why Nestled</span>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-stoneink-900">
            An app that finally makes sense of the stuff you own.
          </h2>
          <p className="mt-3 text-stoneink-700">
            Built for the way real homes work — where things get moved, kids relabel bins, and
            &ldquo;I put it somewhere safe&rdquo; means you&apos;ll never find it again.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.05, duration: 0.4 }}
              className="rounded-3xl border border-sand-300 bg-white p-6 shadow-ambient hover:shadow-hover hover:-translate-y-0.5 transition-all"
              data-testid={`download-feature-${i}`}
            >
              <div className="h-11 w-11 rounded-2xl bg-terracotta-50 text-terracotta-600 grid place-items-center mb-3">
                <f.icon weight="duotone" size={22} />
              </div>
              <h3 className="font-heading text-lg text-stoneink-900 font-medium">{f.title}</h3>
              <p className="mt-2 text-sm text-stoneink-700 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Screenshot band */}
      <section className="bg-stoneink-900 py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 text-white">
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-terracotta-500">A peek inside</span>
              <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
                Same data, everywhere.
              </h2>
              <p className="mt-3 text-white/75 leading-relaxed">
                What you add on the web shows up on your phone, and vice versa. QR labels you print
                at your desk scan instantly with the app. It&apos;s one home, one inventory, on
                whatever screen you happen to be holding.
              </p>
              <a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-band-cta"
                className="mt-6 inline-flex items-center gap-3 h-12 px-5 rounded-2xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-semibold transition-all active:scale-[0.98]"
              >
                <GoogleLogo weight="fill" size={20} /> Get it on Google Play
              </a>
            </div>
            <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[
                "20ya2mjw_Screenshot_20260619-111151.png",
                "7fifyb23_Screenshot_20260619-111528.png",
                "mhiwrqvt_Screenshot_20260619-111538.png",
              ].map((f, i) => (
                <motion.div
                  key={f}
                  initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="rounded-2xl overflow-hidden border border-white/10 shadow-hover aspect-[9/16] bg-stoneink-900"
                  data-testid={`download-shot-${i}`}
                >
                  <img
                    src={`https://customer-assets.emergentagent.com/job_house-organizer-2/artifacts/${f}`}
                    alt={`Nestled screenshot ${i + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-20 text-center">
        <DeviceMobile weight="duotone" size={40} className="mx-auto text-terracotta-600" />
        <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-stoneink-900">
          Ready to stop misplacing things?
        </h2>
        <p className="mt-3 text-stoneink-700 max-w-xl mx-auto">
          Free forever for your first 25 items. Upgrade to Pro or Family when you need more room —
          or don&apos;t. Either way, welcome home.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-bottom-cta"
            className="h-14 pl-4 pr-6 rounded-2xl bg-stoneink-900 hover:bg-stoneink-900/90 text-white flex items-center gap-3 shadow-hover active:scale-[0.98]"
          >
            <GoogleLogo weight="fill" size={28} />
            <div className="text-left leading-tight">
              <div className="text-[10px] uppercase tracking-wider opacity-80">Get it on</div>
              <div className="text-lg font-semibold">Google Play</div>
            </div>
          </a>
          <Link
            to="/try-demo"
            data-testid="download-demo-link"
            className="h-14 px-6 rounded-2xl border border-sand-300 hover:border-terracotta-500 text-stoneink-900 font-medium flex items-center gap-2"
          >
            <Package weight="duotone" size={18} /> Try the web demo first
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
